/**
 * DOCTOR-CLINICAL-WORKSPACE-V1 -- pure view-model helpers for the doctor
 * HOME (/dashboard/doctor). Tested by direct call (repo idiom: no jsdom).
 *
 * The home derives everything from the ONE existing day read
 * (GET /api/appointments/today?view=board) -- no new wire, no invented
 * fields. The consult door routes to the consult workspace; the manual
 * appointment-id entry is DEAD on the doctor surface (NO-MANUAL-ID LAW,
 * guard-pinned).
 */

import type { TodayAppointment, TodayAppointmentsData } from '@/dashboard/types';
import { INTAKE_STATUS_LABELS } from '@/dashboard/patients/patient-360-view';
import {
  deriveDoctorDayModel,
  doctorQueueBandForRow,
  type DoctorDayModel,
} from '@/dashboard/ui/doctor-visit-state';
import { formatDurationHm, formatIstTime, formatIstShortDate } from '@/lib/datetime-ist';
import { rupeesFromMinorLabel } from '@/dashboard/doctor/doctor-consult-view';

// DOCTOR-HOME-STATE-MODEL-V1 (F1): the state derivation this view previously
// carried in TWO local copies (myDayStats + doctorQueueBand -- the "open
// drafts count as In consult" root cause) now lives in THE ONE owner,
// @/dashboard/ui/doctor-visit-state. This module derives the model once and
// threads it; it re-exports the derivation for the page (one computation).
export { deriveDoctorDayModel } from '@/dashboard/ui/doctor-visit-state';
export type { DoctorDayModel } from '@/dashboard/ui/doctor-visit-state';

/** The existing single-clinic read carries scope on the envelope; the
 * multi-clinic read carries it on each row. Never borrow the current UI pick
 * or overwrite an explicit row scope when binding the Home view. */
export function doctorScopedBoardRows(data: TodayAppointmentsData): TodayAppointment[] {
  const clinicId = data.activeClinicId;
  if (typeof clinicId !== 'number' || !Number.isSafeInteger(clinicId) || clinicId <= 0) {
    return data.appointments;
  }
  return data.appointments.map(row => row.clinicId === undefined
    ? { ...row, clinicId }
    : row);
}

export type DoctorQueueClinicView =
  | { status: 'available'; doctorId: number; doctorName: string }
  | { status: 'unavailable' | 'conflict' };

/** A personal-view control needs the separately deployed transport contract. */
export function doctorQueueTransportAvailable(payload: unknown): boolean {
  return !!payload && typeof payload === 'object' && 'byClinic' in payload &&
    !!payload.byClinic && typeof payload.byClinic === 'object' && !Array.isArray(payload.byClinic);
}

/** Defensive consumer of the agreed /me fragment. Unknown wire data never
 * becomes an identity; the server remains responsible for verifying targets. */
export function readDoctorQueueClinicView(payload: unknown, clinicId: number | null): DoctorQueueClinicView {
  const unavailable: DoctorQueueClinicView = { status: 'unavailable' };
  if (clinicId === null || !Number.isSafeInteger(clinicId) || clinicId <= 0 ||
      !payload || typeof payload !== 'object' || !('byClinic' in payload)) return unavailable;
  const map = payload.byClinic;
  if (!map || typeof map !== 'object' || !Object.prototype.hasOwnProperty.call(map, String(clinicId))) return unavailable;
  const item: unknown = Reflect.get(map, String(clinicId));
  if (!item || typeof item !== 'object' || !('status' in item)) return unavailable;
  if (item.status === 'conflict') return { status: 'conflict' };
  if (item.status !== 'available' || !('doctorId' in item) || !('doctorName' in item) ||
      typeof item.doctorId !== 'number' || !Number.isSafeInteger(item.doctorId) || item.doctorId <= 0 ||
      typeof item.doctorName !== 'string' || !item.doctorName.trim()) return unavailable;
  return { status: 'available', doctorId: item.doctorId, doctorName: item.doctorName.trim() };
}

/** L06 preparation: caller supplies a server-verified ID for the permitted
 * clinic. This view predicate grants no access. Null means unavailable identity,
 * distinct from a verified empty queue. */
export function doctorMyPatientRows<
  T extends Pick<TodayAppointment, 'clinicId' | 'assignedDoctorId' | 'status'>,
>(rows: readonly T[], clinicId: number, verifiedDoctorId: number | null): T[] | null {
  if (!Number.isSafeInteger(clinicId) || clinicId <= 0 ||
      verifiedDoctorId === null || !Number.isSafeInteger(verifiedDoctorId) ||
      verifiedDoctorId <= 0) return null;
  return rows.filter((row) => row.clinicId === clinicId && (
    row.assignedDoctorId === verifiedDoctorId ||
    (row.assignedDoctorId === null &&
      (row.status === 'arrived' || row.status === 'checked_in'))
  ));
}

/** Q4: the checked-in FIFO (earliest slot first; the read is time-asc).
 *  Kept as the queue depth (pinned by clinic-day-board.test). */
export function doctorQueueRows(
  rows: readonly TodayAppointment[],
): TodayAppointment[] {
  return rows.filter((r) => r.status === 'checked_in');
}

/**
 * MY DAY header counts (F2, founder walk 2026-08-14) -- Waiting / In consult /
 * Completed / Expected / Cancelled, ALL FIVE from THE ONE state owner
 * (deriveDoctorDayModel), the same derivation every row chip reads -- the
 * header can no longer disagree with the rows it summarizes (one computation,
 * one truth; NEVER a status write to compute a metric -- display only).
 *
 *   - inConsult = THE CHAIR: 0 or 1, never the open-draft count. The founder-
 *     walked root cause ("4-5 In consult" while ONE patient sat in the chair)
 *     was the old local predicate counting every draft sheet; an open draft
 *     that is not being consulted is 'draft_open' (owed paperwork) and never
 *     counts here.
 *   - waiting = physically present (arrived / checked_in), not the chair, not
 *     closed -- whether or not paperwork is open on them.
 *   - completed = THE CONSULTATION IS CLOSED (doctorConsultationClosed -- a
 *     FINALIZED visit), tested FIRST in the owner so a closed row can never
 *     read "in consult" off a stale consult_started event.
 *   - expected = booked / confirmed / rescheduled / waitlist (the same rows
 *     the Expected band lists -- previously a second, separate derivation).
 *   - cancelled = the stored cancelled status (F2 NEW; the Done bar's chip
 *     and this count now read one derivation).
 *   - newPatients / returningPatients (EXISTING-SCREEN-COMPLETIONS-V1) =
 *     the prior-visit IDENTITY fact per row (linkedPatientPriorVisit --
 *     server-derived, the ONE badge owner's input), counted over the same
 *     rows. An identity axis, not a visit state: it rides beside the five
 *     state counts, never inside the state owner.
 *
 * FULFILLED-DERIVATION-CORRECTIVE-V1 (tenant finding 5) stands byte-for-byte:
 * a DESK-set 'fulfilled' / 'completed' without a finalized consultation
 * counts in NO tile and stays VISIBLE in the active queue -- its row now
 * states the honest 'draft_open' / start-owed truth instead of a desk word.
 */
export interface MyDayStats {
  readonly waiting: number;
  readonly inConsult: number;
  readonly completed: number;
  readonly expected: number;
  /** F2 NEW: cancelled visits, derived from the existing stored status. */
  readonly cancelled: number;
  /** EXISTING-SCREEN-COMPLETIONS-V1 (tenant tracker, DOCTOR OP "New
   *  patients / Returning patients"): day counts of the SAME server-derived
   *  prior-visit fact the ONE badge owner renders per row
   *  (linkedPatientPriorVisit; dayBoardNewReturningBadge /
   *  doctorVisitHistoryBadge consume it -- REQ-DOCTOR-DAY-VISIT-HISTORY-
   *  BADGE). Counted over the SAME rows the five founder-ruled counts
   *  summarize. An unlinked or unproven row (fact null) counts in NEITHER
   *  -- the badge's own never-guess posture -- so the pair need not sum to
   *  the day total. */
  readonly newPatients: number;
  readonly returningPatients: number;
  /** A2 (DOCTOR-OS-EXPERIENCE-V1): the NEXT person where a lawful priority
   *  derives -- the first WAITING row by arrival (the same identity class
   *  this board already renders to the doctor). Null when no one waits;
   *  a count alone is insufficient when this can be derived. */
  readonly nextPatient: { readonly name: string | null; readonly time: string } | null;
}

export function myDayStats(
  rows: readonly TodayAppointment[],
  model?: DoctorDayModel,
): MyDayStats {
  const day = model ?? deriveDoctorDayModel(rows);
  let newPatients = 0;
  let returningPatients = 0;
  for (const r of rows) {
    if (r.linkedPatientPriorVisit === 'new') newPatients += 1;
    else if (r.linkedPatientPriorVisit === 'returning') returningPatients += 1;
  }
  let nextPatient: MyDayStats['nextPatient'] = null;
  // The summary and hero recommend the same start-eligible arrival.
  const next = doctorStartableWaitingRows(doctorQueueOrder(rows, day).active, day)[0];
  if (next) nextPatient = {
    name: next.linkedPatientName ?? next.patientName ?? null,
    time: next.arrivedAt ?? next.appointmentTime,
  };
  return { ...day.counts, newPatients, returningPatients, nextPatient };
}

/* ---------------------------------------------------------------------------
 * DOCTOR-RIBBON-FALSE-ZERO-V1 (L-16, docs/AGENT-RUNS/LAKSHMI-BATCH-2-INTAKE-V1.md)
 * -- THE RIBBON SHOWS A ZERO IT DOES NOT KNOW.
 *
 * Dr Lakshmi's frame: "0 New patients / 0 Returning patients" over nine
 * linked rows, two days running. The server's prior-visit derivation had
 * degraded every row's fact to null (its deliberate, correct safety fence),
 * and myDayStats above counts nulls in neither bucket -- so the count of
 * nothing is zero, and the ribbon stated a NUMBER where the value was ABSENT.
 * Zero is a fact about the day. Absent is a fact about the read. A doctor
 * cannot tell them apart from the digit.
 *
 * THREE STATES, NEVER COLLAPSED:
 *   a genuine 0     the day really has no new (or returning) patients -> 0
 *   absent          the derivation degraded -> the honest dash + ONE seam line
 *   not applicable  an unlinked row (a walk-in with no record) -> counts in
 *                   neither bucket, renders no badge -- unchanged, by design
 *
 * THE SIGNAL IS EXPLICIT. The BACKEND decides and says so on the wire
 * (TodayAppointmentsData.priorVisitAvailability); these helpers read THAT
 * and nothing else. All-null is never proof of failure: nine brand-new
 * patients are 9 New / 0 Returning, and a day of unlinked walk-ins is nulls
 * everywhere -- both honest, both 'available'. Inferring "degraded" from the
 * shape of the data would call a real day an outage: the same lie, pointing
 * the other way. An OMITTED signal (an older backend) means available.
 * ------------------------------------------------------------------------ */

/** TRUE only when the wire says the prior-visit derivation did not answer.
 *  Takes the ONE field and nothing else -- no rows, no counts, no arrays. */
export function priorVisitCountsUnavailable(
  availability: TodayAppointmentsData['priorVisitAvailability'],
): boolean {
  return availability === 'unavailable';
}

/** The ONE honest line under the ribbon for the absent state (the primitive's
 *  own `seam` slot -- SAY IT ONCE, never inside each cell). Plain, short and
 *  non-alarming for a doctor mid-clinic: a missing badge, not an outage. The
 *  wording follows the front-desk ribbon's own seam ("Schedule unavailable
 *  right now"). */
export const PRIOR_VISIT_COUNTS_SEAM = 'New and returning counts unavailable right now';

/** The identity cell's value: the count when the day read landed AND the
 *  derivation answered; the honest dash otherwise. A real 0 survives (only
 *  the explicit unavailable signal replaces a number with the dash). */
export function identityCountValue(
  count: number,
  ready: boolean,
  unavailable: boolean,
): number | '-' {
  if (!ready || unavailable) return '-';
  return count;
}

/**
 * CHECKOUT-HANDOFF-AND-DOCTOR-CONTEXT-V1 (QUEUE-ORDER LAW), re-stated by
 * TODAY-PAGE-INTAKE-GATE-CORRECTIVE-V1 item 2: the doctor's queue sorts by
 * ACTIONABILITY, not by the clock -- the patient in the chair and the patient
 * in the waiting room outrank this afternoon's bookings, and the day's
 * finished rows collapse out of the way.
 *
 * THE RULE (founder feedback 2026-07-28: the team could not see why a
 * checked-in patient sorted against a booked-not-arrived one). Every
 * comparison is now EXPLICIT -- the order no longer depends on the read's
 * incoming sequence:
 *
 *   ACTIVE bands (in order; the OWNER's doctorQueueBandForRow is the
 *   authority -- W-5 corrective, founder tower walk 2026-08-14):
 *     0  in consult  -- THE CHAIR (exactly one, owner-arbitrated)
 *     1  present     -- checked in OR arrived: the patient is IN THE CLINIC.
 *                       Ordered by ARRIVAL TIME (whoever got here first is
 *                       seen first), arrival-time-unknown rows last.
 *     2  draft open  -- a desk-closed/departed OPEN DRAFT: owed paperwork
 *                       in its OWN group (W-5: never filed under Expected
 *                       beside not-arrived rows). Ordered by SLOT TIME.
 *     3  expected    -- booked / confirmed / waitlist / rescheduled: not
 *                       here yet. Ordered by SLOT TIME.
 *   Tiebreak within every band: slot time, then appointment id -- a total,
 *   stable, input-order-independent order.
 *
 *   PREVIOUSLY: checked-in and arrived were two SEPARATE bands ordered by
 *   slot time, and the sort relied on the input arriving time-ascending. A
 *   patient who arrived early for a late slot therefore sank below a patient
 *   who had not walked in yet -- the confusion the founder reported.
 *
 *   DONE (the collapsed "Done today" group): the CONSULTATION IS CLOSED (a
 *   FINALIZED visit -- this covers the R-1 ungranted close, which locks the
 *   record while the stored status stays checked_in), OR the visit ENDED
 *   without one (cancelled / no_show). Nothing else leaves the active queue.
 *
 *   FULFILLED-DERIVATION-CORRECTIVE-V1 (tenant finding 5, requirements bank
 *   FRONT-OFFICE-2026-07-28.md B1; founder ruling 2026-07-30 verbatim:
 *   "Doctor Done derives from finalized consultation. Desk-set fulfilled
 *   alone does not mean doctor-complete. Cancelled and no-show behavior
 *   remain unchanged."): a front-desk 'fulfilled' click USED TO collapse the
 *   row into Done today before the doctor had seen the patient. Reception
 *   work finished is not treatment finished, so a desk close WITHOUT a
 *   finalized consultation now stays in the ACTIVE queue -- where the row's
 *   consult door (Start consult on a sheet-less row, Resume consult on a
 *   draft) already exists and is finally usable.
 *
 * Pure; display-only (never a status write). The receptionist board keeps
 * its time order -- this ordering is the DOCTOR lens only, and the DESK
 * lens is untouched: 'fulfilled' still means the desk's work is done there.
 */

/** The stored statuses that put a row in Done today ON THEIR OWN. Both are
 *  TERMINAL-NEGATIVE -- the visit did not happen, so no consultation is left
 *  for the doctor to close. UNCHANGED by the corrective (the ruling's third
 *  sentence). 'fulfilled' and 'completed' are DELIBERATELY ABSENT: they are
 *  DESK closes, and a desk click is not a consultation close. The stored
 *  vocabulary itself is byte-unchanged (R-LABEL) -- no status is renamed and
 *  no row is dropped; a desk-closed row MOVES to the active queue. */
const DONE_TODAY_STATUSES: readonly string[] = ['cancelled', 'no_show'];

/** THE ONE doctor-close signal: the visit is FINALIZED. Both lawful close
 *  paths end here -- the GRANTED close (fulfil -> outcome -> conversion ->
 *  finalize) and the R-1 UNGRANTED close (finalize only, stored status stays
 *  checked_in); see consult-close.ts closeVisit. A fulfilled appointment with
 *  a draft sheet, or with NO case sheet at all, is BY DEFINITION not
 *  finalized -- the doctor still owes the consultation, so the row stays
 *  active. Absent signal (a non-board read, where visitStatus is undefined)
 *  reads NOT closed: the safe direction is a row that stays VISIBLE, never a
 *  row hidden on data that cannot answer the question. */
export function doctorConsultationClosed(row: TodayAppointment): boolean {
  return row.visitStatus === 'finalized';
}

/** Done-today membership for ONE row -- THE single derivation the queue and
 *  the Completed tile both read (one computation, one truth). */
export function doctorRowIsDoneToday(row: TodayAppointment): boolean {
  return (
    DONE_TODAY_STATUSES.includes(row.status) || doctorConsultationClosed(row)
  );
}

export interface DoctorQueueOrder {
  readonly active: TodayAppointment[];
  readonly done: TodayAppointment[];
}

function doctorQueueBand(row: TodayAppointment, model: DoctorDayModel): number {
  // W-5 CORRECTIVE (founder tower walk 2026-08-14): the band is THE OWNER'S
  // authority (doctorQueueBandForRow) -- chair / present-by-arrival /
  // draft_open (desk-closed open paperwork, its OWN group) / expected. A
  // completed-visit-with-open-draft can no longer file under Expected
  // beside not-arrived rows.
  return doctorQueueBandForRow(row, model.chairId);
}

/** An ISO timestamp as a comparable epoch, or null when absent/unparseable.
 *  Parsed (never string-compared) so a differing offset can never mis-order
 *  two real times. */
function queueTimeKey(value: string | null | undefined): number | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/** Ascending, UNKNOWN LAST -- a row with no usable time never displaces a
 *  row that has one. */
function compareQueueTime(a: number | null, b: number | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

/** THE queue comparator: band, then the band's own clock, then slot time,
 *  then id. Total and deterministic -- never dependent on input order. */
function compareDoctorQueueRows(
  a: TodayAppointment,
  b: TodayAppointment,
  model: DoctorDayModel,
): number {
  const byBand = doctorQueueBand(a, model) - doctorQueueBand(b, model);
  if (byBand !== 0) return byBand;
  // Band 1 (present) is ordered by ARRIVAL, not by slot: first here, first
  // seen. Every other band has no arrival fact, so it falls straight through
  // to slot time.
  if (doctorQueueBand(a, model) === 1) {
    const byArrival = compareQueueTime(
      queueTimeKey(a.arrivedAt),
      queueTimeKey(b.arrivedAt),
    );
    if (byArrival !== 0) return byArrival;
  }
  const bySlot = compareQueueTime(
    queueTimeKey(a.appointmentTime),
    queueTimeKey(b.appointmentTime),
  );
  if (bySlot !== 0) return bySlot;
  return a.id - b.id;
}

export function doctorQueueOrder(
  rows: readonly TodayAppointment[],
  model?: DoctorDayModel,
): DoctorQueueOrder {
  const day = model ?? deriveDoctorDayModel(rows);
  const active: TodayAppointment[] = [];
  const done: TodayAppointment[] = [];
  for (const r of rows) {
    if (doctorRowIsDoneToday(r)) {
      done.push(r);
    } else {
      active.push(r);
    }
  }
  active.sort((a, b) => compareDoctorQueueRows(a, b, day));
  return { active, done };
}

// --- The VISIBLE band groups (DOCTOR-OS-BUILD-PHASE-1-V1, brief s.7.1) -------
//
// The merged Doctor OS design renders the active queue as THREE LABELED BANDS
// in the FLOW_187 order -- In consult, Present (by arrival), Expected (by
// slot). The ordering law above is UNTOUCHED: this helper only PARTITIONS an
// already-ordered active queue into its band groups so the home can render
// band titles (the same doctorQueueBand derivation, one computation, one
// truth). Pure; display-only.

export interface DoctorQueueBandGroup {
  readonly key: 'in_consult' | 'present' | 'draft_open' | 'expected';
  /** The band title -- the T1 frame trio plus the W-5 corrective's owed-
   *  paperwork group (the owner's own state word). */
  readonly title: 'In consult' | 'Present' | 'Draft open' | 'Case sheet owed' | 'Expected';
  readonly rows: TodayAppointment[];
}

export function doctorQueueBandGroups(
  orderedActive: readonly TodayAppointment[],
  model?: DoctorDayModel,
): readonly DoctorQueueBandGroup[] {
  const day = model ?? deriveDoctorDayModel(orderedActive);
  const groups: DoctorQueueBandGroup[] = [
    { key: 'in_consult', title: 'In consult', rows: [] },
    { key: 'present', title: 'Present', rows: [] },
    { key: 'draft_open', title: orderedActive.some(row => day.stateById.get(row.id) === 'case_sheet_owed') ? 'Case sheet owed' : 'Draft open', rows: [] },
    { key: 'expected', title: 'Expected', rows: [] },
  ];
  for (const row of orderedActive) {
    groups[doctorQueueBand(row, day)]!.rows.push(row);
  }
  return groups;
}

/** Preserve the present band's arrival order, but recommend only a row whose
 * existing consult door can actually start after check-in and verified identity.
 * Arrived-only or unverified patients stay visible but are never a fallback. */
export function doctorStartableWaitingRows(
  orderedRows: readonly TodayAppointment[],
  model: DoctorDayModel = deriveDoctorDayModel(orderedRows),
): readonly TodayAppointment[] {
  return orderedRows.filter(row => row.status === 'checked_in' &&
    (row.hasPatientRecord === true || row.registrationCaptured === true) &&
    model.stateById.get(row.id) === 'waiting' &&
    consultDoorForRow(row)?.label === 'Start consult');
}

/** The consult door for one queue row. A consultation STARTS once the
 *  person is PRESENT (arrived or checked_in) and the identity-or-
 *  registration gate is open (R-ARRIVAL-FLOW step 5, AUG-12-WALK-BLOCKERS-V1
 *  D4 -- supersedes the 2026-07-09 check-in-first reading IN PLACE); a
 *  visit already recorded resumes/opens regardless of the later stored
 *  status (the sheet is the doctor's record). Rows the doctor cannot act
 *  on yet render chip-only (null door) -- honest, never a dead button. */
export interface ConsultDoor {
  readonly label: 'Start consult' | 'Resume consult' | 'View case sheet';
  readonly emphasis: 'primary' | 'ghost';
}

export function consultDoorForRow(row: TodayAppointment): ConsultDoor | null {
  if (row.visitStatus === 'finalized') {
    return { label: 'View case sheet', emphasis: 'ghost' };
  }
  if (row.visitStatus === 'draft') {
    return { label: 'Resume consult', emphasis: 'primary' };
  }
  if (
    (row.status === 'checked_in' || row.status === 'arrived') &&
    row.consultStarted === true
  ) {
    // R-07 continuity: once the consult_started event exists the door reads
    // Resume (the doctor already opened this consultation), even before the
    // first saved keystroke creates the draft. ARRIVED included (F1 owner
    // alignment with the D4 arrived-is-startable law): a start on an
    // arrived row leaves the stored status 'arrived', and the running
    // consult resumes -- it never re-offers Start.
    return { label: 'Resume consult', emphasis: 'primary' };
  }
  // --- THE START-CONSULT DOOR (LEAD-UNTIL-CONSULT ruling, DOCTOR-OS-
  // EXPERIENCE-V1 2026-08-04; supersedes the two-gate composition of
  // TODAY-PAGE-INTAKE-GATE-CORRECTIVE-V1 + UMR-AT-REGISTRATION-V1, both
  // re-anchored honestly). Every door BELOW this line STARTS a
  // consultation; every door ABOVE it opens an EXISTING record.
  //
  // THE ONE GATE -- IDENTITY-OR-REGISTRATION: the door renders when the row
  // carries EITHER a patient record (hasPatientRecord; the returning
  // patient links, never re-mints) OR a COMPLETE lead registration capture
  // (registrationCaptured; the LEAD-AWAITING-CONSULT state -- the doctor's
  // press CONVERTS the lead through the one writer, mints patient + UMR,
  // and opens the case sheet). Both tested against explicit signals: on a
  // non-board read both fields are ABSENT (undefined), and "unknown" never
  // withholds a lawful door -- the SERVER re-fences every start (409
  // registration_incomplete with the exact missing fields).
  //
  // THE PATIENT-FILLED INTAKE IS NO LONGER A GATE (flow ruling: it renders
  // as clearly-labeled patient-reported REFERENCE; the registration
  // contract on the lead is the precondition). The intake chip stays
  // informational in the row -- see intakeIncompleteBanner.
  if (row.hasPatientRecord === false && row.registrationCaptured !== true) {
    return null;
  }
  // ARRIVED IS STARTABLE (AUG-12-WALK-BLOCKERS-V1, D4 -- R-ARRIVAL-FLOW
  // step 5): an arrived person whose identity-or-registration gate is open
  // waits for the DOCTOR, not for a second front-desk press. Mirrors the
  // server's CONSULT_STARTABLE_STATUSES exactly; the server still re-fences
  // every start (registration_incomplete / identity_review_required).
  if (row.status === 'arrived' || row.status === 'checked_in') {
    return { label: 'Start consult', emphasis: 'primary' };
  }
  // fulfilled/completed with no sheet: the visit happened but no record was
  // started -- still open the workspace so the doctor can author it.
  if (row.status === 'fulfilled' || row.status === 'completed') {
    return { label: 'Start consult', emphasis: 'ghost' };
  }
  return null;
}

/** THE READINESS CHIP (A3, relabeled by CONVERSION-CONSEQUENCE-MINT-V1;
 *  founder ruling 2026-08-08, W-20): registration state and link state are
 *  SEPARATE facts -- a REGISTERED lead awaiting their first consult is
 *  "Ready for consult" (the desk's work is DONE; the mint is the consult's
 *  own consequence), never "registers at consult" (which misreads as
 *  registration still owed). Null for linked patients and unregistered rows
 *  (whose hints speak instead), and null once the consult is already
 *  running or recorded -- a readiness claim on an in-consult row is false. */
export function queueRegistrationChip(row: TodayAppointment): string | null {
  if (
    row.visitStatus === 'draft' ||
    row.visitStatus === 'finalized' ||
    row.consultStarted === true
  ) {
    return null;
  }
  if (row.hasPatientRecord === false && row.registrationCaptured === true) {
    return 'Ready for consult';
  }
  return null;
}

/** For a queue row the doctor cannot open yet (no consult door), the HONEST
 *  reason instead of a blank cell or a dead button. A booked/confirmed row is
 *  waiting for the front desk to check the patient in; an ARRIVED row is
 *  present but not yet checked in (adversarial finding F5: the drawer footer
 *  must never be a blank panel -- every no-door state names its reason).
 *
 *  THE TWO-CHIP DOCTOR VOCABULARY (UMR-AT-REGISTRATION-V1 build item 2; the
 *  same row-hint slot, no StatusChip, no new control):
 *    Gate 1 CLOSED               -> 'Registration pending'   (new string)
 *    Gate 1 OPEN + Gate 2 CLOSED -> 'Intake pending'         (existing string)
 */
export function consultRowHint(row: TodayAppointment): string | null {
  if (consultDoorForRow(row) !== null) return null;
  if (row.status === 'booked' || row.status === 'confirmed') {
    // ROUND-3 COPY DIFF (candidate-b grammar): the mockup's terse seam
    // ("Not arrived") replaces the sentence -- rule 3, wording is design.
    return 'Not arrived';
  }
  // The gate hints below belong ONLY to the statuses whose start door the
  // gate withheld (arrived / checked_in / fulfilled / completed -- arrived
  // became startable with AUG-12-WALK-BLOCKERS-V1 D4, so its no-door state
  // is now the SAME registration reason, not a check-in wait) -- a terminal
  // row (cancelled / no_show) has no door because the visit ENDED, and
  // claiming "Registration pending" on it would invent work that no longer
  // exists (adversarial finding F6).
  if (
    row.status === 'arrived' ||
    row.status === 'checked_in' ||
    row.status === 'fulfilled' ||
    row.status === 'completed'
  ) {
    // The honest non-action (LEAD-UNTIL-CONSULT): neither a patient record
    // nor a complete lead registration exists for this row -- Reception
    // owns the capture; the doctor never recollects it. Rendered through
    // the SAME row-hint slot -- never a dead button, never a silent blank.
    if (row.hasPatientRecord === false && row.registrationCaptured !== true) {
      return 'Registration pending';
    }
  }
  return null;
}

// --- BREAK-GLASS VOCABULARY (UMR-AT-REGISTRATION-V1 build item 4; R-B) ------
//
// The doctor-invoked Gate-2 exception rides the EXISTING consult control (no
// new buttons): when the org's emergency_consult_bypass key is ON, /me
// carries { emergencyConsultBypass: { reasonCodes } } (structurally ABSENT
// with the key OFF) and the start door may offer "Start anyway -- intake
// pending" with a reason dialog reusing the CancelReasonDialog grammar. The
// copy + the banner derivation live HERE (the pure view-model home) so the
// page wiring consumes one vocabulary; the SERVER re-validates key + reason
// on every start and GATE 1 is never bypassable.

/** R-B's doctor-invoked exception label -- verbatim from the ruling. */
export const CONSULT_BYPASS_DOOR_COPY = 'Start anyway - intake pending';

/** The INTAKE INCOMPLETE banner: renders the EXISTING intake-state
 *  vocabulary (patient-360-view INTAKE_STATUS_LABELS -- never a new label).
 *  Null when the wire says the intake is complete, AND null when the signal
 *  is ABSENT (spine flag off / non-board read): absent is UNKNOWN and this
 *  banner asserts "incomplete", so it renders only on a PRESENT explicit
 *  negative -- the same law as the gates (adversarial finding F8). */
export function intakeIncompleteBanner(row: TodayAppointment): string | null {
  if (row.intakeStatus === undefined || row.intakeStatus === 'completed') {
    return null;
  }
  return `Intake incomplete - ${INTAKE_STATUS_LABELS[row.intakeStatus]}`;
}

/** The consult workspace route for a queue row (day's-appointment law: the
 *  queue row IS today's appointment, so the id it carries is today's). */
export function consultPathForAppointment(appointmentId: number): string {
  return `/dashboard/doctor/consult/${appointmentId}`;
}

// --- The header context line (V7-P4-SLICE-2 finding fix b; founder
// live-walk 2026-07-18 via the #275 close -> #285 inputs) -------------------

const HEADER_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/** "19 Jul" from the dashboard read's clinic-day date string ('YYYY-MM-DD',
 *  a date literal -- no timezone math). An unexpected shape echoes the raw
 *  string honestly, never an invented date. */
export function doctorHeaderDateLabel(date: string | null | undefined): string {
  if (typeof date !== 'string') return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  if (match === null) return date;
  const month = HEADER_MONTHS[Number(match[2]) - 1];
  if (month === undefined) return date;
  return `${Number(match[3])} ${month}`;
}

/**
 * The doctor-home header context line: "<clinic> -- <19 Jul>".
 *
 * CLINIC TRUTH: the REAL clinic label from /me clinicOptions (the SAME
 * truth the app-shell front-desk header renders via clinic-context) wins;
 * the dashboard read's clinicName is only the fallback -- it derives from
 * a revenue_daily join and degrades to the "My Clinic" placeholder on any
 * day with no revenue row (the founder-walked defect). Pure; no fetch.
 */
export function doctorHeaderContextLine(args: {
  /** clinic-context label when it names ONE clinic (selectedClinicId set). */
  readonly clinicLabel: string | null;
  /** The dashboard read's clinicName (revenue-join; may be the placeholder). */
  readonly fallbackClinicName: string | null;
  readonly date: string | null;
}): string {
  const clinic = args.clinicLabel?.trim() || args.fallbackClinicName?.trim() || '';
  const day = doctorHeaderDateLabel(args.date);
  return [clinic, day].filter((part) => part !== '').join(' -- ');
}

/**
 * DOCTOR-CONSULT-CORRECTIVE-V1 (BUILD 2, L-06) -- THE HONEST DAY SEGMENT.
 *
 * TRUTHFUL-LABELLING LAW: a surface may not assert a possessive scope it does
 * not have. The header's doctor segment previously took the FIRST non-null
 * assigned name across ALL rows and printed it as the day's owner. That is
 * true only while the board carries ONE doctor; the moment it carries several
 * (Dr. Lakshmi's 2026-09-03 report) the header names ONE doctor over a board
 * containing SEVERAL.
 *
 * This is NOT an authorization change. The board's clinic-wide scope is
 * AUTHORISED by the tenant's own RBAC (ACCESS-RIGHTS-RBAC-2026-06-12.md:38-40)
 * and no doctor predicate is added here. The acceptance criterion this closes
 * is docs/design/journey-role-homes/06-BUILD-HANDOFF.md:181-183 -- "clinic-queue
 * labeling honest ('clinic queue', not 'my patients')".
 *
 * Pure; display-only. Three outcomes, one per real scope:
 *   absent       zero served names -> say nothing (already correct; preserved)
 *   doctor       exactly ONE distinct name -> name them (already correct)
 *   clinic-queue MORE than one -> name NOBODY, say what the board is
 */
export type BoardDoctorSegment =
  | { readonly kind: 'absent' }
  | { readonly kind: 'doctor'; readonly label: string }
  | { readonly kind: 'clinic-queue'; readonly label: string };

/** The prescribed vocabulary (06-BUILD-HANDOFF.md:181-183) -- not invented copy. */
export const CLINIC_QUEUE_LABEL = 'Clinic queue';

export function boardDoctorSegment(
  rows: ReadonlyArray<{ readonly assignedDoctorName?: string | null }>,
): BoardDoctorSegment {
  const distinct = new Set<string>();
  for (const row of rows) {
    const name = row.assignedDoctorName?.trim();
    if (name !== undefined && name !== '') distinct.add(name);
  }
  if (distinct.size === 0) return { kind: 'absent' };
  if (distinct.size === 1) {
    // Non-null assertion is safe: size 1 guarantees one member.
    const [only] = [...distinct];
    return { kind: 'doctor', label: only as string };
  }
  return { kind: 'clinic-queue', label: CLINIC_QUEUE_LABEL };
}

// --- ROUND-3 DNA helpers (DOCTOR-COCKPIT-VISUAL-CONFORM-V1) -------------------
// The candidate-b day grammar, extracted from the mockup SOURCE
// (docs/design-studio/doctor-os/candidate-b/doctors-day.html) per the
// founder's round-3 DESIGN-DNA law. Pure, display-only; every value is a
// served fact or honest absence -- no clock or demographic is ever invented.

/** The mockup's fact separator (a middle dot; authored as an ASCII escape). */
export const MID_DOT = '\u00B7';

/** The chair chip (F1: the chair IS "In consult" -- the word "Draft" belongs
 *  to owed-paperwork rows, never the live consultation): "In consult
 *  <mid-dot> 9m" from the recorded consult_started stamp through THE ONE
 *  duration grammar (F6a -- "9h 38m", never "578m" and never a fabricated
 *  clock); "In consult" alone when the stamp is absent or unusable. */
export function chairChipLabel(
  row: Pick<TodayAppointment, 'consultStartedAt'>,
  nowMs: number,
): string {
  const startedAt = row.consultStartedAt ?? null;
  if (startedAt === null) return 'In consult';
  const ms = Date.parse(startedAt);
  if (Number.isNaN(ms) || ms > nowMs) return 'In consult';
  const minutes = Math.floor((nowMs - ms) / 60_000);
  return `In consult ${MID_DOT} ${formatDurationHm(minutes)}`;
}

/** The hero demographic segment: "34 F" / "34" / "F" from the round-3 wire
 *  facts; null when neither is served (the segment is simply absent). */
export function ageSexLabel(
  ageYears: number | null | undefined,
  gender: string | null | undefined,
): string | null {
  const age =
    typeof ageYears === 'number' && Number.isFinite(ageYears)
      ? String(ageYears)
      : null;
  const g =
    typeof gender === 'string' && gender.trim() !== ''
      ? gender.trim().charAt(0).toUpperCase()
      : null;
  const joined = [age, g].filter((p): p is string => p !== null).join(' ');
  return joined === '' ? null : joined;
}

/** The NEXT hero's queue tail: "then <first name> <mid-dot> <n> more present"
 *  -- n = present patients after the hero (the named one included), the
 *  honest arithmetic reading of the mockup line. Null when nobody follows. */
export function queueTailLabel(afterNames: readonly string[]): string | null {
  // SAME CORRECTION AS chairEmptyLine (2026-09-10): this took the first token
  // of the label and printed "then MOCK" / "then Enquiry". The callers pass
  // `dayBoardPatientLabel(row)`, so the label is used as the queue row uses it.
  const first = afterNames[0];
  if (first === undefined) return null;
  const name = first.trim();
  if (name === '') return null;
  return `then ${name} ${MID_DOT} ${afterNames.length} more present`;
}

/** The worklist state cell: "<state word> <mid-dot> <duration>" over the
 *  SAME shared wait-minutes derivation and THE ONE duration grammar (F6a:
 *  "1h 5m", never "578m" -- one clock, one grammar, this is only the join). */
export function stateWithWaitLabel(statusWord: string, minutes: number): string {
  return `${statusWord} ${MID_DOT} ${formatDurationHm(minutes)}`;
}

/* ---------------------------------------------------------------------------
 * DOCTOR-TODAY-CRAFT-V1 -- THE DAY IN WORDS.
 *
 * The doctor's day board never stated the day's SHAPE anywhere: the ribbon
 * carried seven partial counts and the worklist bands carried their own, but
 * nothing said how big the day was or how much of it was done. A doctor
 * mid-clinic reading "3 Completed" cannot tell whether that is most of the
 * day or the start of it.
 *
 * ONE SENTENCE, from rows ALREADY LOADED -- no new read, no new field.
 * ------------------------------------------------------------------------ */

/**
 * "3 of 5 patients seen" -- the day's progress in one line.
 *
 * THE DENOMINATOR EXCLUDES CANCELLED VISITS ON PURPOSE. A cancelled row is
 * never going to be seen, so counting it as work-still-to-do would make the
 * line permanently unfinishable and quietly wrong on any day with a
 * cancellation. The denominator is therefore the day's REACHABLE visits.
 *
 * Returns null when the day read has not landed, so the caller renders the
 * honest dash rather than "0 of 0" -- a fabricated zero is the one thing this
 * surface's density rules forbid outright.
 */
export function dayProgressLine(
  rows: readonly TodayAppointment[],
  stats: MyDayStats,
  ready: boolean,
): string | null {
  if (!ready) return null;
  const reachable = rows.length - stats.cancelled;
  if (reachable <= 0) return null;
  return `${stats.completed} of ${reachable} patients seen`;
}

/**
 * THE MASTHEAD STORY (S1 OpeningLine clauses), composed for Doctor Today.
 *
 * REF-004 `hercules-doctor-today` (founder-captured, APPROVED 2026-09-01 for
 * structure and workflow only): "the masthead states the day IN WORDS and then
 * the day's shape ... one line, before any metric card". This composes that
 * line for the doctor's two questions -- how much of the day is done, and how
 * much is owed -- and hands it to THE REGISTERED S1 PRIMITIVE
 * (`ui/OpeningLine`), which another lane adopted first. No second opening-line
 * component exists.
 *
 * S1 CONTRACT HELD: every clause maps 1:1 to a figure rendered on this page --
 * the progress clause to the day counts, the owed clause to the owed-work
 * section's own line. Nothing here is generative and no clause carries a free
 * adjective. A figure that has not loaded contributes NO clause rather than a
 * zero, so the line is always true when it is shown and absent when it is not.
 *
 * The chair is deliberately NOT a clause: "In the chair now" is already the
 * loudest panel on the screen, and restating it would be the duplication the
 * count ruling (founder, 2026-09-01) exists to remove.
 */
export function doctorOpeningClauses(
  progressLine: string | null,
  owed: { readonly total: number; readonly oldestDays: number | null } | null,
): string[] {
  const clauses: string[] = [];
  if (progressLine !== null) clauses.push(`${progressLine}.`);
  if (owed !== null && owed.total > 0) {
    const age =
      owed.oldestDays === null
        ? ''
        : owed.oldestDays <= 0
          ? ', the oldest from today'
          : `, the oldest ${owed.oldestDays} ${owed.oldestDays === 1 ? 'day' : 'days'}`;
    clauses.push(
      `${owed.total} ${owed.total === 1 ? 'item' : 'items'} owed${age}.`,
    );
  }
  return clauses;
}

/**
 * The S1 state for the doctor's day.
 *
 * WHY OWED WORK NEVER RAISES `attention`, WHICH IS THE DANGER TONE. A doctor
 * with some documentation outstanding is a doctor on a normal Tuesday. Mapping
 * "anything owed" to danger would paint this masthead red on nearly every day
 * the screen is opened, and a band that is always red is wallpaper -- it stops
 * being read exactly when something is genuinely wrong.
 *
 * The honest alternative would be a threshold ("red past N days"), and there
 * is no ruled one: this vertical's standing rule is that ordering and urgency
 * come from lawful computed states, never from a number an agent picked. So
 * the line STATES the facts and does not grade them: clear reads healthy,
 * outstanding reads quiet, not-yet-loaded reads quiet too. `attention` is left
 * for a surface whose data earns it.
 */
export function doctorOpeningState(
  owed: { readonly total: number } | null,
): 'healthy' | 'attention' | 'quiet' {
  if (owed === null) return 'quiet';
  return owed.total === 0 ? 'healthy' : 'quiet';
}

/* ---------------------------------------------------------------------------
 * DOCTOR-HOME-PARITY-V1 -- the six MISS rows of the parity table, built
 * against the APPROVED frames and nothing else.
 *
 * SOURCE for every string and every state below:
 *   docs/design-studio/doctor-workspace/composed/home-normal.html
 *   docs/design-studio/doctor-workspace/composed/home-dense-day.html
 *   (rendered as png/x-home-normal-1440.png and png/x-home-dense-day-1440.png)
 *
 * NO NEW DESIGN CHOICES. Where the frames do not settle a value it is marked
 * UNSOURCED in the comment and in the run record, never quietly invented.
 * ------------------------------------------------------------------------ */

/** A4 -- "Jubilee Hills - 09:12 IST": where the doctor is, and the time,
 *  in the header. Mono is the frame's treatment (`home-normal.html:141`);
 *  this helper returns only the text. Null when the clinic is unknown --
 *  a clock with no place is not the frame's line. */
export function doctorPlaceClockLine(
  clinicName: string | null | undefined,
  nowIso: string,
): string | null {
  const place = typeof clinicName === 'string' ? clinicName.trim() : '';
  if (place === '') return null;
  const clock = doctorClockLabel(nowIso);
  if (clock === null) return null;
  return `${place} ${MID_DOT} ${clock}`;
}

/** The clock half alone -- "9:12 AM IST".
 *
 *  SAY IT ONCE: the doctor header ALREADY carries the clinic through the
 *  shared clinic-scope primitive ("one carrier for which clinic is this
 *  showing, on every surface"). Where that label is present the header adds
 *  only the missing half, the local time; where it is absent
 *  `doctorPlaceClockLine` composes the frame's full line. */
export function doctorClockLabel(nowIso: string): string | null {
  const time = formatIstTime(nowIso);
  return time === '' ? null : `${time} IST`;
}

/* A12 -- WITHDRAWN, and the reason is recorded rather than the rule kept.
 *
 * This package shipped a wait escalation on thresholds of 20 and 45 minutes.
 * Those were BUILDER CHOICES. The approved frames show example waits (1, 4 and
 * 12 minutes calm; 27, 33, 36 and 41 amber) and an example is not a threshold
 * rule -- it does not say where the boundary lies, and no frame renders a red
 * wait at all.
 *
 * TRACED BEFORE REVERTING, for an existing approved rule or configuration:
 *   - `SLA_BREACH_CLASSES` (lib/sla-leakage/sla-leakage-engine.ts:27-33) has
 *     five classes and every one is LEAD-lifecycle -- unclaimed lead, qualified
 *     with no appointment, booked not confirmed, stuck in stage, no activity.
 *     None is a patient waiting in a clinic.
 *   - the call centre's `WaitingStateTone`
 *     (dashboard/callcenter/callcenter-workspace-view.ts:628) is driven by
 *     those same lead SLA thresholds, so borrowing it would apply a lead rule
 *     to a person in a waiting room.
 *   - `getSlaLeakageBreachThresholds` and
 *     `getLeadAssignmentEscalationMinutes` are the only configured
 *     escalation keys, and both are lead-side.
 *   - main renders a waiting patient DELIBERATELY NEUTRAL:
 *     `.ac-state--waiting { color: hsl(var(--ac-ink-muted)) }` with the glyph
 *     hidden (dashboard.css:16805-16807).
 *
 * NO APPROVED RULE EXISTS. So the wait renders exactly as main renders it --
 * the row's own state tone -- and the gap is recorded in the run record and in
 * the parity table's A12 row. Marking a new rule UNSOURCED does not authorize
 * it, and this file no longer carries one.
 */

/** A13 -- "Slot 09:10 - checked in 09:00 - the wait is clocked from check-in".
 *  The frame prints this as a detail line UNDER the row
 *  (`home-dense-day.html:158`) and as the row's title attribute (`:151`).
 *  Null when the row carries no arrival fact: the sentence's whole point is
 *  that the wait is clocked from an arrival that happened. */
export function waitProvenanceLine(
  row: Pick<TodayAppointment, 'appointmentTime' | 'arrivedAt' | 'currentStatusSince' | 'status'>,
): { readonly short: string; readonly sentence: string } | null {
  const at = row.arrivedAt ?? row.currentStatusSince ?? null;
  if (at === null) return null;
  const slot = formatIstTime(row.appointmentTime);
  const arrived = formatIstTime(at);
  if (slot === '' || arrived === '') return null;
  const word = row.status === 'checked_in' ? 'checked in' : 'arrived';
  const from = word === 'checked in' ? 'check-in' : 'arrival';
  // TWO FORMS, BOTH THE FRAME'S. The approved frame prints the SHORT pair as
  // the row's own title ("Slot 09:10 - checked in 09:00",
  // home-dense-day.html:151) and the full sentence as a FULL-WIDTH detail row
  // beneath the queue row (:158). The state cell is neither of those places --
  // it is narrow -- so the short pair is what renders there and the sentence
  // rides the title. The first build put the sentence in the cell and it
  // clipped mid-word at both widths ("the wait is clo"), which is a defect
  // whatever the words say.
  return {
    short: `Slot ${slot} ${MID_DOT} ${word} ${arrived}`,
    sentence: `Slot ${slot} ${MID_DOT} ${word} ${arrived} ${MID_DOT} the wait is clocked from ${from}`,
  };
}

/** A16 -- "Chair empty - Meera is next" (`transition-home.html:181`). Only
 *  when the chair is genuinely empty AND somebody is actually next; a chair
 *  line naming nobody would be a shell, which the home does not do. */
export function chairEmptyLine(nextPatientName: string | null): string | null {
  // ONE NAME GRAMMAR. The caller passes `dayBoardPatientLabel(row)` -- the same
  // label the queue row prints, with the same honest fallbacks ("Enquiry - name
  // pending", "Unnamed appointment"). This line used to take the FIRST TOKEN of
  // it, which printed "MOCK is next" for "MOCK Meera Krishnan" and would print
  // "Enquiry is next" for a lead whose name is still pending. A screen that
  // names a person one way in the row and another way in the line beside it has
  // two grammars; this one has none of its own.
  const name = typeof nextPatientName === 'string' ? nextPatientName.trim() : '';
  if (name === '') return null;
  return `Chair empty ${MID_DOT} ${name} is next`;
}

/** A20/A21 -- one labelled fact on the next-patient card. */
export interface DoctorNextCardFact {
  readonly key: 'allergy' | 'reports' | 'lastVisit' | 'ledger';
  readonly label: 'Allergy' | 'Reports' | 'Last visit' | 'Ledger';
  /** The rendered value. Null = the fact has no served source, and the row
   *  is DROPPED rather than printed as a guess or an em dash. */
  readonly value: string | null;
  /** A21: the allergy row is the only one that carries a state. */
  readonly tone?: 'positive' | 'danger';
  /** A21's second chip -- "Meds: 3" beside ALLERGY ALERT. */
  readonly extra?: string | null;
}

/**
 * A21 -- the allergy state, from the DURABLE clinical profile the doctor
 * already owns (`PatientClinicalProfileView`, the doctor-only wire that rides
 * the consult-context read this page ALREADY makes for the chair hero).
 *
 * Two states, exactly as the frames draw them:
 *   calm   "None - 14 Jul"     (home-normal.html:204)
 *   alert  "ALLERGY ALERT" + "Meds: 3"  (home-dense-day.html:214)
 *
 * Null when the profile is unavailable -- an ABSENT allergy fact is never
 * rendered as "None", which would be a clinical claim the record has not made.
 */
export function allergyFact(
  profile:
    | {
        readonly anaesthesiaAllergy: 'yes' | 'no' | null;
        readonly medicationAllergies: string | null;
        readonly lastConfirmedAt: string | null;
      }
    | null
    | undefined,
): DoctorNextCardFact | null {
  if (profile === null || profile === undefined) return null;
  const meds = (profile.medicationAllergies ?? '')
    .split(',')
    .map((m) => m.trim())
    .filter((m) => m !== '');
  const medsRecorded = meds.length > 0;

  // ALERT -- either recorded field says there is something to know.
  if (profile.anaesthesiaAllergy === 'yes' || medsRecorded) {
    return {
      key: 'allergy',
      label: 'Allergy',
      value: 'ALLERGY ALERT',
      tone: 'danger',
      extra: medsRecorded ? `Meds: ${meds.length}` : null,
    };
  }

  // NOT AN ALERT -- and this is where the card must be careful.
  //
  // A bare "None" is a claim about EVERY allergy field. The record here holds
  // two separate facts, and an unrecorded one is not a negative one: a profile
  // whose anaesthesia answer is "no" while its medication-allergy field was
  // never filled in has said ONE thing, not two. Folding the silent field into
  // "None" would print a reassurance the record never gave -- on a clinical
  // surface, about allergies, where the cost of a false negative is the whole
  // point of showing it.
  //
  // So the card states exactly what is held, per field:
  //   anaesthesia answered "no", medications unrecorded -> "No anaesthesia allergy"
  //   nothing answered at all                            -> no row
  if (profile.anaesthesiaAllergy === 'no') {
    const confirmed =
      profile.lastConfirmedAt !== null ? formatIstShortDate(profile.lastConfirmedAt) : '';
    const base = 'No anaesthesia allergy';
    return {
      key: 'allergy',
      label: 'Allergy',
      value: confirmed === '' ? base : `${base} ${MID_DOT} ${confirmed}`,
      tone: 'positive',
      extra: null,
    };
  }

  // Never answered either way: the record has no allergy fact, so the card
  // says nothing rather than implying a clean one.
  return null;
}

/**
 * A20 -- the four labelled facts, in the frame's order, with every unserved
 * fact DROPPED. The card renders what the record actually holds.
 */
export function nextCardFacts(input: {
  readonly profile: Parameters<typeof allergyFact>[0];
  readonly reports: string | null | undefined;
  readonly lastVisitSummary: string | null | undefined;
  readonly paidAllVisitsMinorUnits: number | null | undefined;
  readonly visitCount: number | null | undefined;
}): readonly DoctorNextCardFact[] {
  const facts: DoctorNextCardFact[] = [];
  const allergy = allergyFact(input.profile);
  if (allergy !== null) facts.push(allergy);

  const reports = typeof input.reports === 'string' ? input.reports.trim() : '';
  if (reports !== '') {
    facts.push({ key: 'reports', label: 'Reports', value: reports });
  }

  const last = typeof input.lastVisitSummary === 'string' ? input.lastVisitSummary.trim() : '';
  if (last !== '') {
    facts.push({ key: 'lastVisit', label: 'Last visit', value: last });
  }

  const paid = input.paidAllVisitsMinorUnits;
  if (typeof paid === 'number' && paid > 0) {
    const visits = typeof input.visitCount === 'number' && input.visitCount > 0
      ? ` ${MID_DOT} ${input.visitCount} ${input.visitCount === 1 ? 'visit' : 'visits'}`
      : '';
    facts.push({
      key: 'ledger',
      label: 'Ledger',
      value: `${rupeesFromMinorLabel(paid)} paid${visits}`,
    });
  }
  return facts;
}
